import React, { useState, useEffect, useCallback } from 'react';
import { validateSubmissionRate } from '../../utils/validationUtils';
import './RateLimitingWrapper.scss';

const RateLimitingWrapper = ({ 
  children, 
  cooldownMs = 5000,
  maxAttempts = 5,
  timeWindow = 300000, // 5 minutes
  onRateLimitExceeded,
  className = ''
}) => {
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [attemptsInWindow, setAttemptsInWindow] = useState(0);

  // Clean up old submissions outside the time window
  useEffect(() => {
    const now = Date.now();
    const validSubmissions = submissionHistory.filter(
      timestamp => now - timestamp < timeWindow
    );
    
    if (validSubmissions.length !== submissionHistory.length) {
      setSubmissionHistory(validSubmissions);
    }
    
    setAttemptsInWindow(validSubmissions.length);
  }, [submissionHistory, timeWindow]);

  // Check rate limiting status
  const checkRateLimit = useCallback(() => {
    const now = Date.now();
    const recentSubmissions = submissionHistory.filter(
      timestamp => now - timestamp < timeWindow
    );
    
    // Check if we've exceeded max attempts
    if (recentSubmissions.length >= maxAttempts) {
      const oldestSubmission = Math.min(...recentSubmissions);
      const timeUntilReset = timeWindow - (now - oldestSubmission);
      
      setIsRateLimited(true);
      setRemainingTime(timeUntilReset);
      
      if (onRateLimitExceeded) {
        onRateLimitExceeded({
          attempts: recentSubmissions.length,
          maxAttempts,
          timeUntilReset
        });
      }
      
      return false;
    }
    
    // Check cooldown for last submission
    if (recentSubmissions.length > 0) {
      const lastSubmission = Math.max(...recentSubmissions);
      const validation = validateSubmissionRate(lastSubmission, cooldownMs);
      
      if (!validation.isValid) {
        setIsRateLimited(true);
        setRemainingTime(validation.remainingTime);
        return false;
      }
    }
    
    setIsRateLimited(false);
    setRemainingTime(0);
    return true;
  }, [submissionHistory, cooldownMs, maxAttempts, timeWindow, onRateLimitExceeded]);

  // Update remaining time display
  useEffect(() => {
    if (remainingTime > 0) {
      const timer = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1000) {
            checkRateLimit();
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [remainingTime, checkRateLimit]);

  // Record a submission attempt
  const recordSubmission = useCallback(() => {
    const now = Date.now();
    setSubmissionHistory(prev => [...prev, now]);
    checkRateLimit();
  }, [checkRateLimit]);

  // Format remaining time for display
  const formatTime = (ms) => {
    const seconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  };

  // Get rate limit status
  const getRateLimitStatus = () => {
    if (attemptsInWindow >= maxAttempts) {
      return {
        type: 'blocked',
        message: `Too many attempts. Try again in ${formatTime(remainingTime)}`,
        attempts: attemptsInWindow,
        maxAttempts
      };
    }
    
    if (remainingTime > 0) {
      return {
        type: 'cooldown',
        message: `Please wait ${formatTime(remainingTime)} before trying again`,
        attempts: attemptsInWindow,
        maxAttempts
      };
    }
    
    return {
      type: 'ready',
      message: 'Ready to submit',
      attempts: attemptsInWindow,
      maxAttempts
    };
  };

  const status = getRateLimitStatus();

  return (
    <div className={`rate-limiting-wrapper ${className}`}>
      {children}
      
      {/* Rate Limit Status Display */}
      {isRateLimited && (
        <div className={`rate-limit-status ${status.type}`}>
          <div className="rate-limit-icon">
            {status.type === 'blocked' && '🚫'}
            {status.type === 'cooldown' && '⏳'}
          </div>
          <div className="rate-limit-content">
            <div className="rate-limit-message">{status.message}</div>
            {status.type === 'blocked' && (
              <div className="rate-limit-details">
                {status.attempts}/{status.maxAttempts} attempts used
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Progress Indicator */}
      {attemptsInWindow > 0 && (
        <div className="attempts-indicator">
          <div className="attempts-text">
            {attemptsInWindow}/{maxAttempts} attempts used
          </div>
          <div className="attempts-bar">
            <div 
              className="attempts-fill"
              style={{ 
                width: `${(attemptsInWindow / maxAttempts) * 100}%`,
                backgroundColor: attemptsInWindow >= maxAttempts ? '#dc2626' : '#f59e0b'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RateLimitingWrapper;
